import React from "react";
import { ExecutionMetrics } from "@/types/workflow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart3, 
  CheckCircle2, 
  XCircle, 
  Clock,
  TrendingUp
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

interface ExecutionMetricsCardProps {
  metrics: ExecutionMetrics | undefined;
  isLoading: boolean;
}

export const ExecutionMetricsCard: React.FC<ExecutionMetricsCardProps> = ({
  metrics,
  isLoading,
}) => {
  const successRate = metrics && metrics.total_executions > 0
    ? Math.round((metrics.successful / metrics.total_executions) * 100)
    : 0;

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <div className="p-2 bg-primary rounded-lg">
            <BarChart3 className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-lg">Statistieken (24u)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {/* Total Executions */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">Totaal</span>
              </div>
              <p className="text-2xl font-bold">{metrics?.total_executions || 0}</p>
            </div>

            {/* Success Rate */}
            <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-xs">Succes</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                {successRate}%
              </p>
              <p className="text-xs text-muted-foreground">
                {metrics?.successful || 0} van {metrics?.total_executions || 0}
              </p>
            </div>

            {/* Failed */}
            <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-1">
                <XCircle className="h-4 w-4" />
                <span className="text-xs">Mislukt</span>
              </div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                {metrics?.failed || 0}
              </p>
            </div>

            {/* Avg Duration */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs">Gem. duur</span>
              </div>
              <p className="text-2xl font-bold">
                {formatDuration(metrics?.avg_duration_ms || 0)}
              </p>
            </div>
          </div>
        )}

        {metrics?.last_execution && (
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Laatste uitvoering: {formatDistanceToNow(new Date(metrics.last_execution), {
              addSuffix: true,
              locale: nl,
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
